import { useState, type ReactNode } from "react";
import { Box, Card, CardContent, Stack, Typography } from "@mui/material";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import dayjs from "dayjs";
import { LeadsManagementData } from "../hooks/useLeadsManagementData";
import { formatCurrency } from "../constants";
import { colors, shadows, spacing, typography } from "../../../../assets/styles/tokens";
import {
  CustomDot,
  CustomXTick,
} from "../../../AssetManagement/Overview/Trends/TrendsMobile/helpers/components/GraphDesignEl";
import { getYAxisConfig } from "../../../AssetManagement/Overview/Trends/TrendsMobile/helpers/funcs/getYAxisConfig";

interface AnalyticsMobileBreakdownProps {
  data: LeadsManagementData;
}

// Same shadow-token flattening used across this module's mobile cards.
function buildShadow(shadowToken: any): string {
  return shadowToken.$value
    .map((layer: any) => `${layer.offsetX} ${layer.offsetY} ${layer.blur} ${layer.spread} ${layer.color}`)
    .join(", ");
}

// Same Card base as AnalyticsMobileHighLevel's StatCard/LeadMobileCard —
// mt/border/radius/boxShadow and the 8px CardContent padding, so the
// Breakdown charts match the rest of this module's mobile cards exactly.
function ChartCard({ children }: { children: ReactNode }) {
  return (
    <Card
      sx={{
        mt: spacing.Spacing.xl.$value,
        borderRadius: spacing.Radius.md.$value,
        border: `1px solid ${colors.GrayLightMode[200].$value}`,
        boxShadow: buildShadow(shadows.Shadows.sm),
      }}
    >
      <CardContent sx={{ p: spacing.Spacing.md.$value, "&:last-child": { pb: spacing.Spacing.md.$value } }}>
        {children}
      </CardContent>
    </Card>
  );
}

function CardTitle({ children }: { children: ReactNode }) {
  return (
    <Typography
      sx={{
        display: "flex",
        alignItems: "center",
        height: "40px",
        px: spacing.Spacing.xs.$value,
        fontFamily: typography.Font_Family.$value,
        fontWeight: typography.Font_weight.Medium.$value,
        fontSize: typography.Size.Text.sm.$value,
        lineHeight: typography.Line_height.text.sm.$value,
        letterSpacing: "0%",
        color: colors.GrayLightMode[700].$value,
      }}
    >
      {children}
    </Typography>
  );
}

// The line shown under a chart once a bar/point is tapped, e.g.
// "**12** Leads in the **5-7** Range" — a large bold count followed by text.
function ChartSelectionSummary({ count, children }: { count: number; children: ReactNode }) {
  return (
    <Typography
      sx={{
        textAlign: "center",
        mt: spacing.Spacing.md.$value,
        fontFamily: typography.Font_Family.$value,
        fontWeight: typography.Font_weight.Regular.$value,
        fontSize: typography.Size.Text.sm.$value,
        lineHeight: typography.Line_height.text.sm.$value,
        letterSpacing: "0%",
        verticalAlign: "middle",
        color: colors.GrayLightMode[900].$value,
      }}
    >
      <Box
        component="span"
        sx={{
          fontWeight: typography.Font_weight.Semibold.$value,
          fontSize: typography.Size.Text.xl.$value,
          lineHeight: typography.Line_height.text.xl.$value,
        }}
      >
        {count}
      </Box>{" "}
      {children}
    </Typography>
  );
}

function SummaryHighlight({ children }: { children: ReactNode }) {
  return (
    <Box component="span" sx={{ fontWeight: typography.Font_weight.Semibold.$value }}>
      {children}
    </Box>
  );
}

function rangeLowerBound(range: string): number {
  return Number(range.split("-")[0]) || 0;
}

// Same 4 score tiers as the desktop LeadScoreDistributionChart, but with
// this screen's own pastel palette (pulled from the design-token ramps
// rather than the desktop's raw hex) to match the mobile Figma.
const BAND_META: Record<string, { label: string; color: string }> = {
  "1-2": { label: "Weak", color: colors.Error[400].$value },
  "3-4": { label: "Sub-Moderate", color: colors.Blue[600].$value },
  "5-7": { label: "Moderate", color: colors.Brand[600].$value },
  "8-10": { label: "Premium", color: colors.Warning[500].$value },
};

const LEGEND_ORDER = ["1-2", "3-4", "5-7", "8-10"];

function LeadScoreDistributionCardMobile({ data }: AnalyticsMobileBreakdownProps) {
  const { aggregations } = data;
  const [selectedRange, setSelectedRange] = useState<string | null>(null);

  function toggleSelectedRange(range: string) {
    setSelectedRange((current) => (current === range ? null : range));
  }

  const bands = [...aggregations.leadScoreDistribution]
    .sort((a, b) => rangeLowerBound(a.range) - rangeLowerBound(b.range))
    .map((band) => ({
      label: band.range,
      min: rangeLowerBound(band.range),
      count: band.count,
      color: BAND_META[band.range]?.color ?? colors.GrayLightMode[400].$value,
    }));
  const selectedBand = selectedRange ? bands.find((band) => band.label === selectedRange) : null;

  return (
    <ChartCard>
      <CardTitle>Leads Score Distribution</CardTitle>
      <Stack direction="row" flexWrap="wrap" columnGap={spacing.Spacing.md.$value} rowGap={spacing.Spacing.sm.$value} sx={{ mt: spacing.Spacing.md.$value, mb: spacing.Spacing.xl.$value }}>
        {LEGEND_ORDER.map((range) => {
          const isSelected = selectedRange === range;
          return (
            <Stack
              key={range}
              direction="row"
              alignItems="center"
              spacing={spacing.Spacing.sm.$value}
              onClick={() => toggleSelectedRange(range)}
              sx={{
                cursor: "pointer",
                pt: spacing.Spacing.xxs.$value,
                pb: isSelected ? spacing.Spacing.xxs.$value : 0,
                px: spacing.Spacing.xs.$value,
                borderRadius: spacing.Radius.xs.$value,
                bgcolor: isSelected ? BAND_META[range].color : "transparent",
                transition: "background-color 0.15s",
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: isSelected ? colors.Base.White.$value : BAND_META[range].color,
                }}
              />
              <Typography
                sx={{
                  fontFamily: typography.Font_Family.$value,
                  fontWeight: isSelected ? typography.Font_weight.Semibold.$value : typography.Font_weight.Regular.$value,
                  fontSize: typography.Size.Text.xs.$value,
                  color: isSelected ? colors.Base.White.$value : colors.GrayLightMode[600].$value,
                }}
              >
                {BAND_META[range].label}
              </Typography>
            </Stack>
          );
        })}
      </Stack>
      <ResponsiveContainer width="100%" height={190} style={{ marginTop: 8 }}>
        <BarChart data={bands} margin={{ top: 8, right: 0, bottom: 0, left: -30 }} barCategoryGap="25%">
          <CartesianGrid stroke={colors.GrayLightMode[100].$value} vertical={false} />
          <XAxis
            dataKey="label"
            tick={{
              fontFamily: "Inter",
              fontWeight: 500,
              fontSize: typography.Size.Text.xxs.$value,
              fill: colors.GrayLightMode[600].$value,
            }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            allowDecimals={false}
            tick={{
              fontFamily: "Inter",
              fontWeight: 500,
              fontSize: typography.Size.Text.xxs.$value,
              textAnchor: "end",
              fill: colors.GrayLightMode[600].$value,
            }}
            axisLine={false}
            tickLine={false}
          />
          {/* Tapping a bar shows its count below the chart (same as tapping
              its legend item) instead of opening the Leads tab. */}
          <Bar
            dataKey="count"
            barSize={32}
            radius={[3, 3, 0, 0]}
            cursor="pointer"
            onClick={(barProps: any) => toggleSelectedRange(barProps.payload.label)}
          >
            {bands.map((band) => (
              <Cell
                key={band.label}
                fill={band.color}
                fillOpacity={selectedRange && band.label !== selectedRange ? 0.5 : 1}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      {selectedBand && (
        <ChartSelectionSummary count={selectedBand.count}>
          Leads in the <SummaryHighlight>{selectedBand.label}</SummaryHighlight> Range
        </ChartSelectionSummary>
      )}
    </ChartCard>
  );
}

function BrandBarRow({
  label,
  valueLabel,
  percent,
  onClick,
  barMaxWidth,
}: {
  label: string;
  valueLabel: string;
  percent: number;
  onClick?: () => void;
  barMaxWidth?: string;
}) {
  return (
    <Box
      onClick={onClick}
      sx={{ display: "flex", alignItems: "center", cursor: onClick ? "pointer" : "default" }}
    >
      <Typography
        sx={{
          width: 92,
          flexShrink: 0,
          mr: spacing.Spacing.xl.$value,
          fontFamily: "Inter",
          fontWeight: 400,
          fontSize: typography.Size.Text.xxs.$value,
          lineHeight: typography.Line_height.text.xxs.$value,
          letterSpacing: "0%",
          color: colors.GrayLightMode[700].$value,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          flex: 1,
          ...(barMaxWidth ? { maxWidth: barMaxWidth } : {}),
          height: 16,
          mr: spacing.Spacing.md1.$value,
          borderRadius: spacing.Radius.xs.$value,
          bgcolor: colors.Brand[50].$value,
          overflow: "hidden",
        }}
      >
        <Box sx={{ height: "100%", borderRadius: spacing.Radius.xs.$value, bgcolor: colors.Brand[600].$value, width: `${percent}%` }} />
      </Box>
      <Typography
        sx={{
          width: 50,
          flexShrink: 0,
          textAlign: "left",
          fontFamily: "Inter",
          fontWeight: 500,
          fontSize: typography.Size.Text.xxs.$value,
          lineHeight: typography.Line_height.text.xxs.$value,
          letterSpacing: "0%",
          color: colors.GrayLightMode[900].$value,
          whiteSpace: "nowrap",
        }}
      >
        {valueLabel}
      </Typography>
    </Box>
  );
}

// "Total: $x" / "Total Leads: n" header figure, same style as the Income By
// Brand / Leads By Brand card headers.
function HeaderTotal({ label, value, valueColor }: { label?: string; value: ReactNode; valueColor: string }) {
  return (
    <Typography
      sx={{
        fontFamily: typography.Font_Family.$value,
        fontWeight: typography.Font_weight.Medium.$value,
        fontSize: typography.Size.Text.sm.$value,
        lineHeight: typography.Line_height.text.sm.$value,
        letterSpacing: "0%",
        verticalAlign: "middle",
        color: colors.GrayLightMode[700].$value,
      }}
    >
      {label && <Box component="span" sx={{ mr: spacing.Spacing.xs.$value }}>{label}</Box>}
      <Box
        component="span"
        sx={{ mr: spacing.Spacing.md.$value, fontWeight: typography.Font_weight.Semibold.$value, color: valueColor }}
      >
        {value}
      </Box>
    </Typography>
  );
}

// Single-brand view replaces the per-brand income breakdown (one row) with
// just the total.
function TotalIncomeCardMobile({ data }: AnalyticsMobileBreakdownProps) {
  return (
    <ChartCard>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <CardTitle>Total Income:</CardTitle>
        <HeaderTotal
          value={formatCurrency(data.aggregations.incomeByBrand.total)}
          valueColor={colors.Success[600].$value}
        />
      </Box>
    </ChartCard>
  );
}

function IncomeByBrandCardMobile({ data }: AnalyticsMobileBreakdownProps) {
  const { aggregations, goToFilteredLeads, getBrandUidByName } = data;

  const breakdown = [...aggregations.incomeByBrand.byBrand].sort((a, b) => b.amount - a.amount);
  const maxIncome = Math.max(1, ...breakdown.map((row) => row.amount));

  return (
    <ChartCard>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <CardTitle>Income By Brand</CardTitle>
        <Typography
          sx={{
            fontFamily: typography.Font_Family.$value,
            fontWeight: typography.Font_weight.Medium.$value,
            fontSize: typography.Size.Text.sm.$value,
            lineHeight: typography.Line_height.text.sm.$value,
            letterSpacing: "0%",
            verticalAlign: "middle",
            color: colors.GrayLightMode[700].$value,
          }}
        >
          <Box component="span" sx={{ mr: spacing.Spacing.xs.$value }}>Total:</Box>
          <Box
            component="span"
            sx={{
              mr: spacing.Spacing.md.$value,
              fontFamily: typography.Font_Family.$value,
              fontWeight: typography.Font_weight.Semibold.$value,
              fontSize: typography.Size.Text.sm.$value,
              lineHeight: typography.Line_height.text.sm.$value,
              letterSpacing: "0%",
              color: colors.Success[600].$value,
            }}
          >
            {formatCurrency(aggregations.incomeByBrand.total)}
          </Box>
        </Typography>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: spacing.Spacing.lg.$value, mt: spacing.Spacing.xl.$value }}>
        {breakdown.length === 0 ? (
          <Typography sx={{ fontSize: typography.Size.Text.xs.$value, color: colors.GrayLightMode[500].$value }}>
            No brand income recorded yet.
          </Typography>
        ) : (
          breakdown.map((row) => {
            // Not clickable if the brand isn't in the brand directory yet.
            const brandUid = getBrandUidByName(row.brandName);
            return (
              <BrandBarRow
                key={row.brandName}
                label={row.brandName}
                valueLabel={formatCurrency(row.amount)}
                percent={Math.round((row.amount / maxIncome) * 100)}
                onClick={brandUid ? () => goToFilteredLeads({ brand: brandUid }) : undefined}
              />
            );
          })
        )}
      </Box>
    </ChartCard>
  );
}

function LeadsByBrandCardMobile({ data }: AnalyticsMobileBreakdownProps) {
  const { aggregations, goToFilteredLeads } = data;
  const breakdown = aggregations.byBrand
    .map((item) => ({ brandUid: item.brandUid, brand: item.brandName, count: item.count }))
    .sort((a, b) => b.count - a.count);
  const maxCount = Math.max(1, ...breakdown.map((row) => row.count));
  const totalLeads = breakdown.reduce((sum, row) => sum + row.count, 0);

  return (
    <ChartCard>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <CardTitle>Leads By Brand</CardTitle>
        <Typography
          sx={{
            fontFamily: typography.Font_Family.$value,
            fontWeight: typography.Font_weight.Medium.$value,
            fontSize: typography.Size.Text.sm.$value,
            lineHeight: typography.Line_height.text.sm.$value,
            letterSpacing: "0%",
            verticalAlign: "middle",
            color: colors.GrayLightMode[700].$value,
          }}
        >
          <Box component="span" sx={{ mr: spacing.Spacing.xs.$value }}>Total Leads:</Box>
          <Box
            component="span"
            sx={{
              mr: spacing.Spacing.md.$value,
              fontWeight: typography.Font_weight.Semibold.$value,
              color: colors.GrayLightMode[900].$value,
            }}
          >
            {totalLeads}
          </Box>
        </Typography>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: spacing.Spacing.lg.$value, mt: spacing.Spacing.lg.$value, mx: spacing.Spacing.xs.$value }}>
        {breakdown.map((row) => (
          <BrandBarRow
            key={row.brandUid}
            label={row.brand}
            valueLabel={String(row.count)}
            percent={Math.round((row.count / maxCount) * 100)}
            onClick={() => goToFilteredLeads({ brand: row.brandUid })}
            barMaxWidth="65%"
          />
        ))}
      </Box>
    </ChartCard>
  );
}

function toIsoDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function buildDayRange(startIso: string, endIso: string): Date[] {
  const start = new Date(`${startIso}T00:00:00`);
  const end = new Date(`${endIso}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) return [];
  const days: Date[] = [];
  for (let t = start.getTime(); t <= end.getTime(); t += 86400000) {
    days.push(new Date(t));
  }
  return days;
}

// Same layout as the Trends mobile charts (e.g. CACChart): the Y axis is
// pinned in its own narrow chart on the left, and the plot scrolls sideways
// at a fixed 60px per day once there are more than 7 days, so every date
// keeps its own readable label instead of being thinned out.
const LEADS_BY_DAY_HEIGHT = 242;
const LEADS_BY_DAY_PX_PER_DAY = 60;

function LeadsByDayCardMobile({ data, showTotal = false }: AnalyticsMobileBreakdownProps & { showTotal?: boolean }) {
  const { aggregations, start, end } = data;
  const seriesColor = colors.Brand[600].$value;
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const countByDate = new Map(aggregations.byDay.map((item) => [item.date, item.count]));
  const chartData = buildDayRange(start, end).map((day) => {
    const iso = toIsoDate(day);
    return { iso, x: dayjs(iso).format("DD MMM"), y: countByDate.get(iso) ?? 0 };
  });
  const yAxisConfig = getYAxisConfig(chartData);
  const selectedDay = activeIndex !== null ? chartData[activeIndex] : null;

  return (
    <ChartCard>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <CardTitle>Leads By Day</CardTitle>
        {showTotal && (
          <HeaderTotal
            label="Total Leads:"
            value={aggregations.byDay.reduce((sum, item) => sum + item.count, 0)}
            valueColor={colors.GrayLightMode[900].$value}
          />
        )}
      </Box>
      {chartData.length === 0 ? (
        <Box sx={{ height: LEADS_BY_DAY_HEIGHT, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography
            sx={{
              fontSize: typography.Size.Text.sm.$value,
              fontWeight: typography.Font_weight.Medium.$value,
              color: colors.GrayLightMode[700].$value,
            }}
          >
            No Data
          </Typography>
        </Box>
      ) : (
        <Box sx={{ width: "100%", display: "flex", alignItems: "stretch" }}>
          <Box
            sx={{
              width: 43,
              flexShrink: 0,
              position: "sticky",
              left: 0,
              zIndex: 2,
              bgcolor: colors.Base.White.$value,
            }}
          >
            <ResponsiveContainer width="100%" height={LEADS_BY_DAY_HEIGHT}>
              <AreaChart data={chartData} margin={{ top: 16, right: 0, left: -8, bottom: 24 }}>
                <XAxis dataKey="x" tick={false} tickLine={false} axisLine={false} interval={0} height={48} />
                <YAxis
                  domain={yAxisConfig.domain}
                  ticks={yAxisConfig.ticks}
                  allowDecimals={false}
                  tick={{
                    fill: colors.GrayLightMode[600].$value,
                    fontSize: typography.Size.Text.xxs.$value,
                    fontWeight: typography.Font_weight.Medium.$value,
                  }}
                  tickLine={false}
                  axisLine={false}
                  width={35}
                  tickMargin={3}
                />
              </AreaChart>
            </ResponsiveContainer>
          </Box>
          <Box sx={{ flex: 1, overflowX: "auto", overflowY: "hidden" }}>
            <Box
              sx={{
                minWidth: chartData.length > 7 ? `${chartData.length * LEADS_BY_DAY_PX_PER_DAY}px` : "100%",
              }}
            >
              <ResponsiveContainer width="100%" height={LEADS_BY_DAY_HEIGHT}>
                <AreaChart
                  data={chartData}
                  margin={{ top: 16, right: 24, left: 10, bottom: 24 }}
                  onClick={(state: any) => {
                    const index = state?.activeTooltipIndex;
                    setActiveIndex((prev) => (index === undefined || prev === index ? null : index));
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <defs>
                    <linearGradient id="leadsByDayMobileGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={seriesColor} stopOpacity={0.5} />
                      <stop offset="100%" stopColor={colors.Brand[50].$value} stopOpacity={0.09} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid vertical={false} stroke={colors.GrayLightMode[300].$value} opacity={0.3} />
                  <XAxis
                    dataKey="x"
                    tick={(tickProps: any) => <CustomXTick {...tickProps} activeIndex={activeIndex} />}
                    tickLine={false}
                    axisLine={false}
                    interval={0}
                    height={48}
                    tickMargin={10}
                  />
                  <YAxis domain={yAxisConfig.domain} ticks={yAxisConfig.ticks} hide />
                  <RechartsTooltip
                    cursor={{ stroke: colors.GrayLightMode[200].$value, strokeWidth: 1 }}
                    content={({ active, payload }: any) =>
                      active && payload?.length ? (
                        <Box
                          sx={{
                            bgcolor: colors.Base.White.$value,
                            borderRadius: spacing.Radius.xs.$value,
                            boxShadow: "0px 4px 16px rgba(0,0,0,0.12)",
                            boxSizing: "border-box",
                            minWidth: 25,
                            height: 26,
                            px: spacing.Spacing.xs.$value,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: typography.Font_Family.$value,
                              fontWeight: typography.Font_weight.Bold.$value,
                              fontSize: typography.Size.Text.xs.$value,
                              lineHeight: typography.Line_height.text.xs.$value,
                              letterSpacing: "0%",
                              textAlign: "center",
                              color: colors.Brand[700].$value,
                            }}
                          >
                            {payload[0].value}
                          </Typography>
                        </Box>
                      ) : null
                    }
                  />
                  <Area
                    type="monotone"
                    dataKey="y"
                    stroke={seriesColor}
                    strokeWidth={1.5}
                    fill="url(#leadsByDayMobileGradient)"
                    dot={(dotProps: any) => (
                      <CustomDot key={dotProps.index} {...dotProps} activeIndex={activeIndex} color={seriesColor} />
                    )}
                    activeDot={false}
                    isAnimationActive={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        </Box>
      )}
      {selectedDay && (
        <ChartSelectionSummary count={selectedDay.y}>
          Lead{selectedDay.y === 1 ? "" : "s"} on{" "}
          <SummaryHighlight>{dayjs(selectedDay.iso).format("DD MMM YYYY")}</SummaryHighlight>
        </ChartSelectionSummary>
      )}
    </ChartCard>
  );
}

// Breakdown tab — the 4 aggregation charts from the desktop Analytics tab
// (Lead Score Distribution/Income By Brand/Leads By Day/Leads By Brand),
// rebuilt as stacked mobile cards rather than reusing the desktop
// components as-is, since the layouts diverge (desktop is a 2-column grid
// of wide Paper cards; mobile stacks single-column compact cards).
export default function AnalyticsMobileBreakdown({ data }: AnalyticsMobileBreakdownProps) {
  // With one brand filtered, the by-brand breakdowns would only ever show a
  // single row, so they collapse into totals instead (same rule as desktop).
  const singleBrand = data.brand !== "All";
  return (
    <Box sx={{ pb: spacing.Spacing.xl.$value }}>
      <LeadScoreDistributionCardMobile data={data} />
      {singleBrand ? <TotalIncomeCardMobile data={data} /> : <IncomeByBrandCardMobile data={data} />}
      <LeadsByDayCardMobile data={data} showTotal={singleBrand} />
      {!singleBrand && <LeadsByBrandCardMobile data={data} />}
    </Box>
  );
}
